import { type IterationAttributes } from './generated/IterationAttributes';
import { get } from 'lodash';
import moment from 'moment';
import ApplicationModel, { attr } from '../ApplicationModel';
import Fragment from '../Fragment';
import recordEvents, {
  type IterationStartEnd,
} from '../lib/RecordEvents/recordEvents';
import { model } from '../modelBuilder';
import {
  type RecordEventFilters,
  type RecordEventValueOperationEnum,
  type RecordId,
} from './generated/types';
import * as Enums from '../Enums';
import { type UnitType } from '../lib/Estimate';
import { HasExtensionFields } from './concerns/HasExtensionFields';

interface Iteration extends IterationAttributes, HasExtensionFields {}

@model
@HasExtensionFields
class Iteration extends ApplicationModel {
  // Start auto-generated
  static typename: 'Iteration' = 'Iteration';
  get typename(): 'Iteration' {
    return 'Iteration';
  }

  static internalTypename: 'Iteration' = 'Iteration';
  get internalTypename(): 'Iteration' {
    return 'Iteration';
  }

  static terminologyName = 'Iteration';

  static inputType = 'IterationAttributes!';

  static filterType = 'IterationFilters!';
  static filters = {
    id: '[ID!]',
    projectId: 'ID',
    programIncrementIterationId: 'ID',
    status: '[Int!]',
  };

  static fields = {
    commentsCount: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    duration: attr({ writable: true, readable: true }),
    endDate: attr({ readable: true }),
    id: attr({ readable: true }),
    name: attr({ writable: true, readable: true }),
    path: attr({ readable: true }),
    progress: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    riskFactorsCount: attr({ readable: true }),
    startDate: attr({ writable: true, readable: true }),
    status: attr({ writable: true, readable: true }),
    updatedAt: attr({ readable: true }),
    capacity: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    description: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Note',
    }),
    iterationProgress: attr.belongsTo({
      readable: true,
      typename: 'IterationProgress',
    }),
    originalEstimate: attr.belongsTo({ readable: true, typename: 'Estimate' }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    programIncrement: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrement',
    }),
    programIncrementIteration: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrementIteration',
    }),
    project: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Project',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    comments: attr.hasMany({ readable: true }),
    extensionFields: attr.hasMany({
      args: { filters: 'ExtensionFieldFilters' },
      readable: true,
    }),
    records: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    teamMembers: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  static STATUS_PLANNING = 10;
  static STATUS_STARTED = 20;
  static STATUS_COMPLETED = 30;

  static UNITS_COUNT: UnitType = 'COUNT';
  static UNITS_POINTS: UnitType = 'POINTS';
  static UNITS_MINUTES: UnitType = 'MINUTES';

  static BURNDOWN_AVAILABLE_DATE = moment('2021-11-19');

  constructor(attributes, options = {}) {
    super(attributes, options);

    this.local = {};
  }

  get endDate() {
    return moment(this.startDate).add(this.duration, 'days').toDate();
  }

  set endDate(newValue) {
    const duration = moment(newValue).diff(this.startDate, 'days');
    if (duration < 0) {
      this.startDate = newValue;
      this.duration = 0;
    } else {
      this.duration = duration;
    }
  }

  get completedRecords() {
    return this.records.filter(
      record => record.teamWorkflowStatus?.isCompleted
    );
  }

  compare(value, otherValue) {
    return value < otherValue ? -1 : value > otherValue ? 1 : 0;
  }

  compareStartDate(other: this) {
    return this.compare(this.startDate, other.startDate);
  }

  get planning() {
    return this.status === Iteration.STATUS_PLANNING;
  }

  get started() {
    return this.status === Iteration.STATUS_STARTED;
  }

  get completed() {
    return this.status === Iteration.STATUS_COMPLETED;
  }

  get defaultEstimationType() {
    return this.originalEstimate?.value ? 'original' : 'count';
  }

  get statusText() {
    switch (this.status) {
      case Iteration.STATUS_PLANNING:
        return 'Planning';
      case Iteration.STATUS_STARTED:
        return 'In progress';
      case Iteration.STATUS_COMPLETED:
        return 'Completed';
    }
  }

  async start() {
    return this.changeStatus(Iteration.STATUS_STARTED);
  }

  /**
   * @param options
   * @param options.moveIncompleteId The Id of an iteration to move unfinished work to
   * @returns true if successful
   */
  async complete(
    options: { moveIncompleteId?: string } = {}
  ): Promise<boolean> {
    const { moveIncompleteId } = options;

    if (moveIncompleteId !== undefined) {
      const data = await ApplicationModel.mutate(
        new Fragment('completeIteration')
          .argument('id', this.id)
          .argument('moveIncompleteId', moveIncompleteId)
          // Reload this iteration with the existing query + status
          .fragment(this.query.select('status').buildQueryFragment('iteration'))
      );

      const path = ['completeIteration', 'iteration'];

      if (get(data, path)) {
        // If it was successful then there should be attributes to reload
        this.resetAttributes(get(data, path));
      } else {
        // If it was not successful then reload the status
        await this.loadAttributes('status');
      }

      return get(data, 'completeIteration.errors.isEmpty', false);
    } else {
      return this.changeStatus(Iteration.STATUS_COMPLETED);
    }
  }

  /**
   * Reopen a completed iteration
   * @returns true if successful
   */
  async reopen(): Promise<boolean> {
    const data = await ApplicationModel.mutate(
      new Fragment('reopenIteration')
        .argument('id', this.id)
        // Reload this iteration with the existing query + status
        .fragment(this.query.select('status').buildQueryFragment('iteration'))
    );

    const path = ['reopenIteration', 'iteration'];

    if (get(data, path)) {
      // If it was successful then there should be attributes to reload
      this.resetAttributes(get(data, path));
    } else {
      // If it was not successful then reload the status
      await this.loadAttributes('status');
    }

    return get(data, 'reopenIteration.errors.isEmpty', false);
  }

  async changeStatus(
    newStatus:
      | typeof Iteration.STATUS_PLANNING
      | typeof Iteration.STATUS_STARTED
      | typeof Iteration.STATUS_COMPLETED
  ) {
    const previousStatus = this.status;
    this.status = newStatus;

    const success = await this.save();
    if (!success) {
      this.status = previousStatus;
    }

    return success;
  }

  get url() {
    return `/iterations/${this.id}`;
  }

  /**
   * API takes capacity text only
   */
  capacityForQuery(value) {
    return value?.text;
  }

  /**
   * Fetch the start and end record events
   */
  async eventsStartEnd(): Promise<IterationStartEnd> {
    if (!this.local.eventsStartEnd)
      this.local.eventsStartEnd = await recordEvents.iterationStartEnd(this.id);

    return this.local.eventsStartEnd;
  }

  /**
   * Fetch a time series for this iteration
   *
   * @param userId optional userId
   * @param aggregation
   * @param startDate
   * @param filters Any filters to apply to the raw data fetch.
   */
  async eventsTimeSeries(
    userId?: RecordId,
    aggregation?: RecordEventValueOperationEnum['type'],
    startDate?: string,
    filters: Partial<RecordEventFilters> = {}
  ) {
    const dates = await this.eventsStartEnd();
    if (!dates.startEvent && !startDate) return null;

    filters = {
      ...filters,
      iterationId: this.id,
      createdAt: {
        gt: startDate || dates.startEvent?.createdAt,
        lt: moment(dates.endEvent?.createdAt || new Date())
          .add('1', 'minute')
          .toISOString(),
      },
      units: this.originalEstimate?.units,
    };

    if (userId) {
      filters.assignedToUserId = userId;
    }

    if (!aggregation) {
      aggregation = Enums.RecordEventValueOperationEnum.SUM;
    }

    return recordEvents.recordEventTimeSeries(
      filters,
      Enums.RecordEventTimeSeriesGroupEnum.DAY,
      aggregation
    );
  }

  /**
   * Fetch raw events for the iteration.
   *
   * @param filters Any filters to apply to the raw data fetch.
   */
  async eventsRaw(filters: Partial<RecordEventFilters> = {}) {
    return recordEvents.recordEventsRaw({ ...filters, iterationId: this.id });
  }
}

export default Iteration;
